import { useEffect, useState } from "react";
import { Loader2, FileText, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { fmtUSD } from "./PfsPrimitives";

export default function EmailToLenderDialog({ open, onOpenChange, pfs, totals }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(`Personal Financial Statement — ${pfs?.name || "Borrower"}`);
      setMessage("");
    }
  }, [open, pfs?.name]);

  const send = async (e) => {
    e?.preventDefault();
    const recipients = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      toast.error("Enter at least one recipient");
      return;
    }
    const ccList = cc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    setSending(true);
    try {
      await api.post("/pfs/email", {
        to: recipients,
        cc: ccList.length ? ccList : null,
        subject: subject || null,
        message: message || null,
      });
      toast.success(`PFS emailed to ${recipients.join(", ")}`);
      onOpenChange(false);
      setTo("");
      setCc("");
      setMessage("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="pfs-email-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" /> Email PFS to lender
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sends the latest saved PFS as a PDF attachment with a snapshot of total assets, liabilities, and net worth in the body.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={send} className="space-y-3">
          <label className="block">
            <div className="overline text-slate-600 mb-1">Recipient(s) — comma-separated</div>
            <input
              type="text" required autoFocus
              className="input-clean w-full"
              value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="loan-officer@bank.com, underwriter@bank.com"
              data-testid="pfs-email-to"
            />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">CC (optional)</div>
            <input type="text" className="input-clean w-full" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="pfs-email-cc" />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">Subject</div>
            <input type="text" className="input-clean w-full" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pfs-email-subject" />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">Message (optional)</div>
            <textarea
              className="input-clean w-full min-h-[100px]"
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal note for your lender — e.g., 'As discussed on our call today...'"
              maxLength={2000}
              data-testid="pfs-email-message"
            />
          </label>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs font-mono">
            <div className="text-slate-500 mb-1 uppercase tracking-widest text-[10px]">Snapshot included</div>
            <div className="text-slate-700">Total assets: <span className="font-bold">{fmtUSD(totals?.total_assets)}</span></div>
            <div className="text-slate-700">Total liabilities: <span className="font-bold">{fmtUSD(totals?.total_liabilities)}</span></div>
            <div className="text-emerald-700">Net worth: <span className="font-bold">{fmtUSD(totals?.net_worth)}</span></div>
          </div>
          <DialogFooter className="flex items-center gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="btn-outline-dark inline-flex items-center gap-1 px-3" data-testid="pfs-email-cancel">
              <X className="w-4 h-4" /> Cancel
            </button>
            <button type="submit" disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="pfs-email-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Send PDF
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
